import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { makeZodI18nMap } from 'zod-i18n-map'
import { HookFormTextField } from '@portal/pkg-hook-form'
import { SideDrawer } from '@portal/pkg-ui'
import { RoleSchema, RoleFormValues, roleDefaults, DESCRIPTION_MAX } from '../model/roleSchema'
import { DEFAULT_ROLE_NAME } from '../model/builtInRoles'
import { entityTypeLabel } from '../model/entityTypeLabel'
import { useCreateRole, useUpdateRole } from '../mutations'
import { RoleQueries } from '../queries'

const PROBLEM_PREFIX = 'https://gomercatus.com/problems/admin/roles/'
const ALL_ENTITY_TYPES = 'all'

/**
 * The refusals worth naming to the user. Everything else falls back to the generic message: the
 * server rejects far more than the drawer can put a sentence to, and a wrong sentence is worse
 * than a plain one.
 *
 * A switch rather than a lookup table so that each arm's return stays a literal, which is what
 * the generated i18n key union needs to accept it.
 */
function messageKeyFor(problemType: string | undefined) {
  switch (problemType) {
    case `${PROBLEM_PREFIX}role-name-not-unique`:
      return 'roles.form.nameNotUnique'
    case `${PROBLEM_PREFIX}role-name-reserved`:
      return 'roles.form.nameReserved'
    case `${PROBLEM_PREFIX}built-in-role-not-modifiable`:
      return 'roles.form.builtInNotModifiable'
    case `${PROBLEM_PREFIX}entity-type-scope-feature-disabled`:
      return 'roles.form.scopeFeatureDisabled'
    case `${PROBLEM_PREFIX}unknown-organization-entity-types`:
      return 'roles.form.unknownEntityTypes'
    default:
      return 'roles.form.saveFailed'
  }
}

/**
 * The editable fields plus the scope state that must survive a save. Both a grid row and a fetched
 * role satisfy it, so either entry point preserves scoped Entity access.
 */
export interface EditableRole {
  id: number
  name: string
  description?: string | null
  entityTypeAllAccess: boolean
  entityTypeIds?: number[] | null
}

interface RoleFormProps {
  /** The role being edited, or undefined when adding one. */
  role?: EditableRole
  onClose: () => void
}

export function RoleForm({ role, onClose }: RoleFormProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const isEdit = role !== undefined
  const isDefaultRole = role?.name === DEFAULT_ROLE_NAME
  const scopeSettings = useQuery(RoleQueries.scopeSettings())
  const scopeEnabled = scopeSettings.data?.entityTypeScopedAuthorizationEnabled === true
  const entityTypes = useQuery({
    ...RoleQueries.entityTypes(),
    enabled: scopeEnabled,
  })
  const needsRoleDetail =
    scopeEnabled &&
    role !== undefined &&
    !isDefaultRole &&
    !role.entityTypeAllAccess &&
    role.entityTypeIds == null
  const roleDetail = useQuery({
    ...RoleQueries.detail(role?.id ?? 0),
    enabled: needsRoleDetail,
  })

  const form = useForm<RoleFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(RoleSchema, { errorMap: makeZodI18nMap({ handlePath: false }) }),
    defaultValues: role
      ? {
          name: role.name,
          description: role.description ?? '',
          entityTypeAllAccess: isDefaultRole || role.entityTypeAllAccess,
          entityTypeIds: isDefaultRole ? null : (role.entityTypeIds ?? null),
        }
      : roleDefaults,
  })

  const {
    handleSubmit,
    control,
    resetField,
    setValue,
    formState: { dirtyFields, isValid },
  } = form
  const entityTypeAllAccess = useWatch({ control, name: 'entityTypeAllAccess' })

  useEffect(() => {
    if (needsRoleDetail && !dirtyFields.entityTypeIds && roleDetail.data?.entityTypeIds != null) {
      setValue('entityTypeIds', roleDetail.data.entityTypeIds)
    }
  }, [dirtyFields.entityTypeIds, needsRoleDetail, roleDetail.data, setValue])

  /**
   * `mutate` returns as soon as it is called, so react-hook-form's own `isSubmitting` is false
   * again while the request is still in flight and would leave the button live for a second
   * submission.
   */
  const isScopeLoading =
    scopeEnabled && (entityTypes.isLoading || (needsRoleDetail && roleDetail.isLoading))
  const isSaving = createRole.isPending || updateRole.isPending

  const onError = (error: unknown) => {
    const axiosError = error as {
      response?: { data?: { type?: string; errors?: Array<{ field?: string }> } }
    }
    const problem = axiosError.response?.data
    const problemType = problem?.type
    if (problemType === `${PROBLEM_PREFIX}entity-type-scope-feature-disabled`) {
      const originalAllAccess = isDefaultRole || (role?.entityTypeAllAccess ?? true)
      // This field has no rendered input to register it, so resetField would not update its value.
      setValue('entityTypeAllAccess', originalAllAccess, { shouldValidate: true })
      resetField('entityTypeIds', {
        defaultValue: originalAllAccess ? null : (role?.entityTypeIds ?? null),
      })
      void scopeSettings.refetch()
    }
    const invalidScope =
      problemType === `${PROBLEM_PREFIX}role-request-invalid` &&
      problem?.errors?.some(
        (fieldError) =>
          fieldError.field === 'entityTypeAllAccess' || fieldError.field === 'entityTypeIds',
      )
    enqueueSnackbar(
      t(invalidScope ? 'roles.form.invalidEntityTypeScope' : messageKeyFor(problemType)),
      { variant: 'error' },
    )
  }

  const onSubmit = (values: RoleFormValues) => {
    const onSuccess = () => {
      enqueueSnackbar(t(isEdit ? 'roles.form.roleUpdated' : 'roles.form.roleAdded'), {
        variant: 'success',
      })
      onClose()
    }
    if (role) {
      updateRole.mutate({ id: role.id, ...values }, { onSuccess, onError })
    } else {
      createRole.mutate(values, { onSuccess, onError })
    }
  }

  return (
    <SideDrawer
      title={t(isEdit ? 'roles.drawer.editTitle' : 'roles.drawer.addTitle')}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={1}>
          <Button color="secondary" onClick={onClose}>
            {t('roles.drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isValid || isSaving}
            onClick={() => void handleSubmit(onSubmit)()}
          >
            {t('roles.drawer.save')}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...form}>
        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <HookFormTextField<RoleFormValues>
                name="name"
                label={t('roles.form.name')}
                variant="outlined"
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <HookFormTextField<RoleFormValues>
                name="description"
                label={t('roles.form.description')}
                variant="outlined"
                fullWidth
                size="small"
                slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
              />
            </Grid>
            {scopeSettings.isLoading && (
              <Grid size={{ xs: 12 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CircularProgress size={18} />
                  <FormHelperText>{t('roles.form.roleSettingsLoading')}</FormHelperText>
                </Stack>
              </Grid>
            )}
            {scopeSettings.isLoadingError && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => void scopeSettings.refetch()}
                    >
                      {t('roles.retry')}
                    </Button>
                  }
                >
                  {t('roles.form.roleSettingsFailed')}
                </Alert>
              </Grid>
            )}
            {isScopeLoading && (
              <Grid size={{ xs: 12 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CircularProgress size={18} />
                  <FormHelperText>{t('roles.form.entitiesLoading')}</FormHelperText>
                </Stack>
              </Grid>
            )}
            {scopeEnabled && entityTypes.isLoadingError && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={() => void entityTypes.refetch()}>
                      {t('roles.retry')}
                    </Button>
                  }
                >
                  {t('roles.entityTypes.loadFailed')}
                </Alert>
              </Grid>
            )}
            {scopeEnabled && entityTypes.data && !isScopeLoading && (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="entityTypeIds"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const allAccess = entityTypeAllAccess
                    const selectedIds = allAccess ? [] : (field.value ?? [])
                    const value: Array<number | string> = allAccess
                      ? [ALL_ENTITY_TYPES]
                      : selectedIds
                    const known = new Map(
                      entityTypes.data.map((entityType) => [entityType.id, entityType]),
                    )
                    const choices = entityTypes.data.filter(
                      (entityType) => entityType.enabled || selectedIds.includes(entityType.id),
                    )
                    const unknownIds = selectedIds.filter((id) => !known.has(id))
                    const scopeUnavailable =
                      needsRoleDetail && roleDetail.data?.entityTypeIds == null

                    const labelFor = (id: number) => entityTypeLabel(t, id, known)

                    return (
                      <FormControl fullWidth size="small" error={!!error}>
                        <InputLabel id="role-entity-types-label">
                          {t('roles.form.entityTypes')}
                        </InputLabel>
                        <Select
                          {...field}
                          disabled={isDefaultRole}
                          labelId="role-entity-types-label"
                          label={t('roles.form.entityTypes')}
                          multiple
                          value={value}
                          onChange={(event) => {
                            const requested = event.target.value as Array<number | string>
                            if (isDefaultRole || (allAccess && requested.length === 0)) {
                              return
                            }
                            const selectedAll = requested.includes(ALL_ENTITY_TYPES)
                            if (selectedAll && !allAccess) {
                              setValue('entityTypeAllAccess', true, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                              setValue('entityTypeIds', null, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                              return
                            }
                            const ids = requested
                              .filter((item): item is number => typeof item === 'number')
                              .filter((id, index, values) => values.indexOf(id) === index)
                              .sort((left, right) => left - right)
                            setValue('entityTypeAllAccess', false, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                            setValue('entityTypeIds', ids, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }}
                          renderValue={(selected) =>
                            selected
                              .map((item) =>
                                item === ALL_ENTITY_TYPES
                                  ? t('roles.entityTypes.all')
                                  : labelFor(item as number),
                              )
                              .join(', ')
                          }
                        >
                          <MenuItem value={ALL_ENTITY_TYPES}>{t('roles.entityTypes.all')}</MenuItem>
                          {choices.map((entityType) => (
                            <MenuItem key={entityType.id} value={entityType.id}>
                              {labelFor(entityType.id)}
                            </MenuItem>
                          ))}
                          {unknownIds.map((id) => (
                            <MenuItem key={id} value={id}>
                              {labelFor(id)}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText error={!!error}>
                          {(error?.message === 'roles.form.entityTypesRequired'
                            ? t('roles.form.entityTypesRequired')
                            : error?.message) ??
                            (scopeUnavailable
                              ? t('roles.form.entityTypesUnavailable')
                              : isDefaultRole
                                ? t('roles.form.defaultRoleEntitiesHelp')
                                : t('roles.form.entityTypesHelp'))}
                        </FormHelperText>
                      </FormControl>
                    )
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </FormProvider>
    </SideDrawer>
  )
}
